import { useEffect, useRef, useState } from "react"
import classNames from "classnames"
import { useOutsideClick } from "@hooks"
import * as React from "react"

interface DropdownItem {
  id: string
  name: string
  imageUrl?: string
}

interface DropdownProps {
  id: string
  title?: string
  data: DropdownItem[]
  position?: "bottom-right" | "bottom-left" | "top-right" | "top-left"
  hasImage?: boolean
  style?: string
  selectedIds?: string[] // Changed from single selectedId to array
  onSelect?: (ids: string[], type: "senior" | "midLevel") => void // Returns multiple selected IDs
  isMultiSelect?: boolean // New: Multi-select mode
  type: "senior" | "midLevel"
}

export const Dropdown = ({
  id,
  title = "Select",
  data,
  position = "bottom-left",
  hasImage,
  style,
  selectedIds = [],
  onSelect,
  type,
  isMultiSelect = false, // Default to single-select mode
}: DropdownProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [selectedItems, setSelectedItems] = useState<DropdownItem[]>([])

  useEffect(() => {
    if (selectedIds && data) {
      const newSelectedItems = data.filter((item) =>
        selectedIds.includes(item.id)
      )
      setSelectedItems(newSelectedItems)
    } else {
      setSelectedItems([])
    }
  }, [])

  const handleChange = (item: DropdownItem) => {
    let updatedSelection: DropdownItem[]

    if (isMultiSelect) {
      if (selectedItems.some((selected) => selected.id === item.id)) {
        updatedSelection = selectedItems.filter(
          (selected) => selected.id !== item.id
        ) // Deselect
      } else {
        updatedSelection = [...selectedItems, item] // Select multiple
      }
    } else {
      if (selectedItems.some((selected) => selected.id === item.id)) {
        updatedSelection = [] // Deselect if already selected
      } else {
        updatedSelection = [item] // Single select
      }
      setIsOpen(false)
    }

    setSelectedItems(updatedSelection)
    onSelect &&
      onSelect(
        updatedSelection.map((i) => i.id),
        type
      )
  }

  const dropdownRef = useRef<HTMLDivElement>(null)
  useOutsideClick({
    ref: dropdownRef,
    handler: () => setIsOpen(false),
  })

  const dropdownClass = classNames(
    "absolute bg-white w-full max-h-52 overflow-y-scroll overflow-y-visible py-3 rounded shadow-md z-10",
    {
      "top-full right-0 mt-2": position === "bottom-right",
      "top-full left-0 mt-2": position === "bottom-left",
      "bottom-full right-0 mb-2": position === "top-right",
      "bottom-full left-0 mb-2": position === "top-left",
    }
  )

  return (
    <div ref={dropdownRef} className="relative">
      <button
        id={id}
        aria-label="Toggle dropdown"
        aria-haspopup="true"
        aria-expanded={isOpen}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={classNames(
          "flex justify-between items-center lg:text-sm text-xs gap-5 !rounded-lg w-full py-2 px-4 bg-transparent border-linear-gradient text-black",
          style
        )}
      >
        <span>
          {selectedItems.length === 1
            ? selectedItems.map((item) => item.name).join(", ")
            : title}
        </span>
        <p
          className={classNames("transform duration-500 ease-in-out", {
            "rotate-180": isOpen,
          })}
        >
          ⌵
        </p>
      </button>

      {/* Open */}
      {isOpen && (
        <div aria-label="Dropdown menu" className={dropdownClass}>
          <ul
            role="menu"
            aria-labelledby={id}
            aria-orientation="vertical"
            className="leading-10"
          >
            <li
              onClick={() => {
                setSelectedItems([]);
                onSelect && onSelect([], type);
                setIsOpen(false);
              }}
              className="flex items-center cursor-pointer hover:bg-gray-200 px-3"
            >
              <span className="flex-1">None</span>
            </li>
            {data?.map((item) => {
              const isSelected = selectedItems.some(
                (selected) => selected.id === item.id
              )
              return (
                <li
                  key={item.id}
                  onClick={() => handleChange(item)}
                  className={classNames(
                    "flex items-center cursor-pointer hover:bg-gray-200 px-3",
                    { "bg-gray-300": isSelected }
                  )}
                >
                  {hasImage && (
                    <img
                      src={item.imageUrl}
                      alt="image"
                      loading="lazy"
                      className="w-8 h-8 rounded-full bg-gray-400 object-cover me-2"
                    />
                  )}
                  <span className="flex-1">{item.name}</span>
                  {isMultiSelect && (
                    <input
                      type="checkbox"
                      checked={isSelected}
                      className="w-4 h-4 ml-2"
                      readOnly
                    />
                  )}
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}
